import { QueryOptions, WithRequired } from '@tanstack/react-query';

import {
  getCustomerFeedbacksForStore,
  getCustomerFeedbacksForUsecase,
  getKlaviyoAccount,
  getSavedVideoScripts,
  getShopifyShop,
  getStoreAggregatedMetrics,
  getStoreDailyMetrics,
  getUsecase,
  getUsecasesForStore,
  postShopifyCartProductImages,
} from 'src/api/methods';

type QueryMapOption<T> = T extends (...args: any) => infer R
  ? WithRequired<QueryOptions<Awaited<R>>, 'queryKey'>
  : never;

export const queries = {
  usecase: (usecaseId: number): QueryMapOption<typeof getUsecase> => ({
    queryKey: ['usecase', usecaseId],
    queryFn: () => getUsecase(usecaseId),
  }),
  usecasesForStore: (storeKey: string | undefined): QueryMapOption<typeof getUsecasesForStore> => ({
    queryKey: ['usecases', storeKey],
    queryFn: () => getUsecasesForStore(storeKey),
  }),
  storeAggregatedMetrics: (
    storeKey: string,
    days: number,
    useCaseId?: number,
  ): QueryMapOption<typeof getStoreAggregatedMetrics> => ({
    queryKey: ['store', storeKey, 'usecase', useCaseId, 'aggregate-metrics', days],
    queryFn: () => getStoreAggregatedMetrics(storeKey, days, useCaseId),
  }),
  storeDailyMetrics: (
    storeKey: string,
    days: number,
    useCaseId?: number,
  ): QueryMapOption<typeof getStoreDailyMetrics> => ({
    queryKey: ['store', storeKey, 'usecase', useCaseId, 'daily-metrics', days],
    queryFn: () => getStoreDailyMetrics(storeKey, days, useCaseId),
  }),
  customerFeedbacksForUsecase: (
    usecaseId: number,
    page = 1,
    pageSize = 10,
  ): QueryMapOption<typeof getCustomerFeedbacksForUsecase> => ({
    queryKey: ['usecase', 'user-feedback', usecaseId, page, pageSize],
    queryFn: () => getCustomerFeedbacksForUsecase(usecaseId, page, pageSize),
  }),
  customerFeedbacksForStore: (
    storeId: number | undefined,
    page = 1,
    pageSize = 25,
  ): QueryMapOption<typeof getCustomerFeedbacksForStore> => ({
    queryKey: ['store', 'user-feedback', storeId, page, pageSize],
    queryFn: () => getCustomerFeedbacksForStore(storeId, page, pageSize),
  }),
  klaviyoAccount: (klaviyoAccountId: number): QueryMapOption<typeof getKlaviyoAccount> => ({
    queryKey: ['klaviyo-account', klaviyoAccountId],
    queryFn: () => getKlaviyoAccount(klaviyoAccountId),
  }),
  shopifyShop: (shopifyShopId: number): QueryMapOption<typeof getShopifyShop> => ({
    queryKey: ['shopify-shop', shopifyShopId],
    queryFn: () => getShopifyShop(shopifyShopId),
  }),
  shopifyCartProductImages: (
    storeKey: string,
    shopifyCheckoutId: string | undefined | null,
    shopifyCheckoutToken: string | undefined | null,
    shopifyProductIds: string[],
  ): QueryMapOption<typeof postShopifyCartProductImages> => ({
    queryKey: [
      'store',
      storeKey,
      'shopify-cart-product-images',
      shopifyCheckoutId,
      shopifyCheckoutToken,
      shopifyProductIds,
    ],
    queryFn: () =>
      postShopifyCartProductImages(
        storeKey,
        shopifyCheckoutId,
        shopifyCheckoutToken,
        shopifyProductIds,
      ),
  }),
  savedVideoScripts: (useCaseId: number): QueryMapOption<typeof getSavedVideoScripts> => ({
    queryKey: ['usecase', useCaseId, 'get-saved-video-scripts'],
    queryFn: () => getSavedVideoScripts(useCaseId),
  }),
};
